import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from "../../components/layout/index";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { Calendar, ChevronLeft, ChevronRight, Pencil, Eye, Shield, UserPlus, Users, User, Phone, Mail, BadgeCheck, ToggleLeft, Settings, Stethoscope, Search, RotateCcw, SlidersHorizontal, CheckCircle, XCircle } from "lucide-react";


/**
 * Curebay Doctor List Component
 * This component displays the list of Curebay doctors
 */
const CurebayDoctorList = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'active', 'inactive'
    const [searchInput, setSearchInput] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [debouncedEmail, setDebouncedEmail] = useState('');
    const searchDebounceRef = useRef(null);
    const [statusFilter, setStatusFilter] = useState(''); // '', 'INITIATED', 'REGISTERED', 'VERIFIED', 'ACTIVE', 'INACTIVE'
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const statusFilterRef = useRef(null);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const datePickerRef = useRef(null);
    const today = new Date();
    const defaultStartOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultEndOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const [createdFrom, setCreatedFrom] = useState(format(defaultStartOfMonth, "yyyy-MM-dd"));
    const [createdTo, setCreatedTo] = useState(format(defaultEndOfMonth, "yyyy-MM-dd"));
    const [selectedRange, setSelectedRange] = useState({
        start: defaultStartOfMonth,
        end: defaultEndOfMonth,
    });
    const [month, setMonth] = useState(today.getMonth());
    const [year, setYear] = useState(today.getFullYear());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [togglingDoctorId, setTogglingDoctorId] = useState(null);
    const [togglingVerifiedDoctorId, setTogglingVerifiedDoctorId] = useState(null);
    const [verifyConfirmDoctor, setVerifyConfirmDoctor] = useState(null);
    const [toggleStatusConfirm, setToggleStatusConfirm] = useState(null); // { doctor, nextStatus }
    const [financeApprovalConfirm, setFinanceApprovalConfirm] = useState(null); // { doctor, financeApprovalStatus, label }
    const [financeApprovalLoadingId, setFinanceApprovalLoadingId] = useState(null);
    const [rejectRemark, setRejectRemark] = useState("");
    const [rejectRemarkView, setRejectRemarkView] = useState(null); // { doctor, remark }
    const role = localStorage.getItem("role") || "";
    const roleList = role.split(",").map((r) => r.trim()).filter(Boolean);
    const isFinanceRole = roleList.includes("FINANCE");
    const [viewDoctorDetails, setViewDoctorDetails] = useState(null);
    const [viewDoctorLoading, setViewDoctorLoading] = useState(false);
    const [photoModal, setPhotoModal] = useState({
        isOpen: false,
        type: null,
        currentPhoto: null,
        photoUrl: null,
    });
    const inFlightFetchKeysRef = useRef(new Set());
    const tableSectionRef = useRef(null);
    const [tableContainerWidth, setTableContainerWidth] = useState(null);

    useEffect(() => {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
            setDebouncedEmail(emailInput.trim());
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchInput, emailInput]);

    useEffect(() => {
        fetchList();
    }, [filter, statusFilter, createdFrom, createdTo, currentPage, debouncedSearch, debouncedEmail]);

    useEffect(() => {
        const el = tableSectionRef.current;
        if (!el) return;
        const measure = () => setTableContainerWidth(el.offsetWidth);
        requestAnimationFrame(measure);
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [loading, doctors.length]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (statusFilterRef.current && !statusFilterRef.current.contains(e.target)) {
                setStatusFilterOpen(false);
            }
            if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
                setDatePickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatDisplayDate = (date) => {
        if (!date) return "";
        try {
            return format(date, "dd-MM-yyyy");
        } catch {
            return "";
        }
    };

    const formatDateLabel = (date) => {
        if (!date) return "";
        return format(date, "dd-MM-yyyy");
    };

    const formatSelectedDateLabel = () => {
        const { start, end } = selectedRange;
        if (start && end) return `${formatDateLabel(start)} — ${formatDateLabel(end)}`;
        if (start) return formatDateLabel(start);
        return "Date";
    };
    const consultationTypeMap = {
        V: "Video",
        I: "In-Person",
        C: "Camp",
        H: "Home Visit",
    };
    const formatConsultationType = (consultationType) => {
        if (!consultationType) return "-";
        const values = Array.isArray(consultationType)
            ? consultationType
            : String(consultationType)
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);

        return values
            .map((value) => consultationTypeMap[value] || value)
            .join(", ");
    };

    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const handlePrevMonth = () => {
        setMonth((prev) => {
            if (prev === 0) {
                setYear((y) => y - 1);
                return 11;
            }
            return prev - 1;
        });
    };

    const handleNextMonth = () => {
        setMonth((prev) => {
            if (prev === 11) {
                setYear((y) => y + 1);
                return 0;
            }
            return prev + 1;
        });
    };

    const handleDayClick = (day) => {
        const current = new Date(year, month, day);
        const { start, end } = selectedRange;

        if (!start || (start && end)) {
            setSelectedRange({ start: current, end: null });
            setCreatedFrom("");
            setCreatedTo("");
            return;
        }

        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const currentDateOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());

        if (currentDateOnly < startDateOnly) {
            setSelectedRange({ start: current, end: startDateOnly });
            setCreatedFrom(format(currentDateOnly, "yyyy-MM-dd"));
            setCreatedTo(format(startDateOnly, "yyyy-MM-dd"));
        } else {
            setSelectedRange({ start: startDateOnly, end: currentDateOnly });
            setCreatedFrom(format(startDateOnly, "yyyy-MM-dd"));
            setCreatedTo(format(currentDateOnly, "yyyy-MM-dd"));
        }

        setCurrentPage(1);
    };

    const fetchList = async () => {
        const token = localStorage.getItem("auth_token");
        let requestKey = "";

        try {
            const params = new URLSearchParams();
            params.set("page", String(currentPage - 1)); // API is 0-based
            params.set("size", String(pageSize));
            
            // Status filter from icon dropdown takes precedence; else use All/Active/Inactive
            if (statusFilter) {
                params.set("status", statusFilter);
            } else if (filter === 'active') {
                params.set("status", "ACTIVE");
            } else if (filter === 'inactive') {
                params.set("status", "INACTIVE");
            }

            if (debouncedSearch) {
                params.set("search", debouncedSearch);
            }
            if (debouncedEmail) {
                params.set("email", debouncedEmail);
            }

            // Created date range filters
            // if (createdFrom) {
            //     params.set("createdFrom", createdFrom);
            // }
            // if (createdTo) {
            //     params.set("createdTo", createdTo);
            // }

            requestKey = params.toString();
            if (inFlightFetchKeysRef.current.has(requestKey)) {
                return;
            }
            inFlightFetchKeysRef.current.add(requestKey);
            setLoading(true);

            const baseUrl = API_URL?.endsWith('/') ? API_URL : `${API_URL}/`;
             const tenantId = localStorage.getItem('tenantId');
            const res = await fetch(
                `${baseUrl}doctor/onboard-requests?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "accept": "*/*",
                        "Content-Type": "application/json",
                        ...(token && { Authorization: `Bearer ${token}` }),
                    //    ...(tenantId && { tenantId }),
                    },
                }
            );
            
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            
            const data = await res.json();
            const items = Array.isArray(data) 
                ? data 
                : data?.content || data?.items || [];
            
            setDoctors(items || []);
         
            setTotalElements(data?.totalElements);
        } catch (error) {
            console.error("Failed to fetch doctor data:", error);
            toast.error("Failed to fetch doctor data");
            setDoctors([]);
            setTotalElements(0);
        } finally {
            if (requestKey) {
                inFlightFetchKeysRef.current.delete(requestKey);
            }
            setLoading(false);
        }
    };

    const getDoctorId = (doctor) => doctor?.doctorUserId || doctor?.doctorCode || doctor?.id;
    const getDoctoremail = (doctor) => doctor?.email;
    const getNextActiveStatus = (doctor) => {
        const currentStatus = (doctor?.status || '').toUpperCase();
        if (currentStatus === 'ACTIVE') return 'INACTIVE';
        if (currentStatus === 'INACTIVE') return 'ACTIVE';
        return null;
    };

    const openToggleStatusConfirm = (doctor) => {
        const nextStatus = getNextActiveStatus(doctor);
        // if (!nextStatus) {
        //     toast.info('Status can be toggled only from ACTIVE/INACTIVE.');
        //     return;
        // }
        setToggleStatusConfirm({ doctor, nextStatus });
    };

    const getFullName = (doctor) => {
        const parts = [doctor?.firstName, doctor?.middleName, doctor?.lastName].filter(Boolean);
        return parts.join(' ').trim() || doctor?.fullName || '-';
    };

    const formatQualifications = (qualifications) => {
        if (!qualifications || (Array.isArray(qualifications) && qualifications.length === 0)) return '-';
        return Array.isArray(qualifications) ? qualifications.join(', ') : String(qualifications);
    };

    const formatSpecialization = (specialization) => {
        if (!specialization || (Array.isArray(specialization) && specialization.length === 0)) return '-';
        return Array.isArray(specialization) ? specialization.join(', ') : String(specialization);
    };

    const formatLicenseNumber = (licenseNumber) => {
        if (!licenseNumber) return '-';
        const cleaned = String(licenseNumber).replace(/\D/g, '');
        if (cleaned.length === 12) {
            return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 8)} ${cleaned.slice(8, 12)}`;
        }
        return licenseNumber;
    };

    const getStatusStyle = (status) => {
        const s = (status || '').toUpperCase();
        if (s === 'ACTIVE') return 'bg-emerald-100 text-emerald-700 font-medium';
        if (s === 'VERIFIED') return 'bg-sky-100 text-sky-700 font-medium';
        if (s === 'REGISTERED') return 'bg-blue-100 text-blue-700 font-medium';
        if (s === 'INITIATED') return 'bg-amber-100 text-amber-700 font-medium';
        if (s === 'INACTIVE') return 'bg-red-100 text-red-700 font-medium';
        return 'bg-gray-100 text-gray-600';
    };

    // Active/Inactive: based on explicit flag or status
    const getActiveInactive = (doctor) => {
        if (doctor?.active !== undefined) return doctor.active ? 'Active' : 'Inactive';
        const s = (doctor?.status || '').toUpperCase();
        return s === 'ACTIVE' ? 'Active' : 'Inactive';
    };

    const getActiveInactiveStyle = (doctor) => {
        const isActive = doctor?.active ?? ((doctor?.status || '').toUpperCase() === 'ACTIVE');
        return isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600';
    };

    const formatStatusLabel = (status) => {
        return String(status || '')
            .toLowerCase()
            .split('_')
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    };

    const getFinanceStatusLabel = (financeApprovalStatus) => {
        if (financeApprovalStatus === 1) return 'Finance Approved';
        if (financeApprovalStatus === 2) return 'Finance Rejected';
        return 'Waiting for Finance Approval';
    };

    const getFinanceStatusStyle = (financeApprovalStatus) => {
        if (financeApprovalStatus === 1) return 'bg-emerald-100 text-emerald-700 font-medium';
        if (financeApprovalStatus === 2) return 'bg-red-100 text-red-700 font-medium';
        return 'bg-amber-100 text-amber-700 font-medium';
    };

    // Verified or Not: based on explicit flag or status
    const getVerifiedStatus = (doctor) => {
        if (doctor?.verified !== undefined) return doctor.verified ? 'Verified' : 'Not Verified';
        if (doctor?.isVerified !== undefined) return doctor.isVerified ? 'Verified' : 'Not Verified';
        const s = (doctor?.status || '').toUpperCase();
        return s === 'VERIFIED' ? 'Verified' : 'Not Verified';
    };

    const getVerifiedStyle = (doctor) => {
        const isVerified = doctor?.verified ?? doctor?.isVerified ?? ((doctor?.status || '').toUpperCase() === 'VERIFIED');
        return isVerified ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600';
    };

    // Calculate pagination
    const totalPages = Math.ceil(totalElements / pageSize);
    const startEntry = totalElements === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
    const endEntry = Math.min(currentPage * pageSize, totalElements);

    // Generate page numbers (show up to 7 pages)
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 7;
        
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 4) {
                for (let i = 1; i <= maxVisible; i++) {
                    pages.push(i);
                }
            } else if (currentPage >= totalPages - 3) {
                for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                for (let i = currentPage - 3; i <= currentPage + 3; i++) {
                    pages.push(i);
                }
            }
        }
        return pages;
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleAddDoctor = () => {
        navigate('/CurebayDoctorVerify');
    };

    const handleOnboarding = (doctor) => {
        // Normalize a few fields into the shape the onboarding form expects
        const mappedDoctor = {
            doctorName: getFullName(doctor),
            licenseNumber: doctor.medicalLicenseNumber || "",
            qualifications: doctor.qualification || "",
            specialization: Array.isArray(doctor.specialization)
                ? doctor.specialization.filter(Boolean)
                : doctor.specialization
                    ? [doctor.specialization]
                    : [],
            experience:
                doctor.yearsOfExperience != null
                    ? String(doctor.yearsOfExperience)
                    : "",
            phone: doctor.mobile || "",
            email: doctor.email || "",
            newDoctorRequestId: doctor.id || "",
        };

        navigate('/CurebayDoctorVerify', {
            state: {
                // Keep every field from the self-registration record (PAN, qualification,
                // language selection, status, etc.) alongside the normalized fields above
                // so the Verify page can display the doctor's full submitted details.
                doctorData: { ...doctor, ...mappedDoctor },
                isEdit: true,
            },
        });
    };
    const handleEdit = async (doctor) => {
        const profileId = doctor?.doctorProfileId || getDoctorId(doctor);
        const email = doctor?.email;
        if (!profileId) {
            toast.error('Doctor profile ID not found');
            return;
        }
        try {
            const token = localStorage.getItem('auth_token');
            const tenantId = doctor?.tenantId;
            const baseUrl = API_URL?.endsWith('/') ? API_URL : `${API_URL}/`;
            const res = await fetch(`${baseUrl}doctor/profile/${email}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...(tenantId && { tenantId }),
                },
            });
            if (!res.ok) {
                throw new Error('Failed to fetch doctor profile');
            }
            const profileData = await res.json();
            console.log(profileData,"profileData");
            const mergedDoctorData = profileData?.data ?? profileData ?? doctor;
            navigate('/CurebayDoctorEdit', {
                state: {
                    doctorData: {
                        ...mergedDoctorData,
                        stateMedicalCouncil:
                            mergedDoctorData?.stateMedicalCouncil || doctor?.stateMedicalCouncil || "",
                    },
                    isEdit: true,
                },
            });
        } catch (err) {
            console.error('Failed to fetch doctor profile:', err);
            toast.error('Failed to load doctor profile');
            navigate('/CurebayDoctorEdit', {
                state: {
                    doctorData: doctor,
                    isEdit: true,
                },
            });
        }
    };

    const handleView = async (doctor) => {
        console.log(doctor,"doctoruygiuh");
        const profileId = doctor?.doctorProfileId || getDoctorId(doctor);
        const email = doctor?.email;
        if (!profileId) {
            toast.error('Doctor profile ID not found');
            return;
        }
        setViewDoctorLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            const tenantId = doctor?.tenantId;
            const baseUrl = API_URL?.endsWith('/') ? API_URL : `${API_URL}/`;
            const res = await fetch(`${baseUrl}doctor/profile/${email}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...(tenantId && { tenantId }),
                },
            });
            if (!res.ok) {
                throw new Error('Failed to fetch doctor details');
            }
            const data = await res.json();
            setViewDoctorDetails(data?.data ?? data ?? doctor);
        } catch (err) {
            console.error('Failed to fetch doctor details:', err);
            toast.error('Failed to load doctor details');
            setViewDoctorDetails(doctor);
        } finally {
            setViewDoctorLoading(false);
        }
    };

    const DOCUMENT_PHOTO_FIELDS = [
        { key: 'profilePhoto', label: 'Profile Photo' },
        { key: 'adharPhoto', label: 'Aadhar Photo' },
        { key: 'panPhoto', label: 'PAN Photo' },
        { key: 'medicalRegistrationCertificatePhoto', label: 'Medical Registration Certificate' },
        { key: 'cancelledChequePhoto', label: 'Cancelled Cheque Photo' },
        { key: 'signaturePhoto', label: 'Signature Photo' },
        { key: 'stampPhoto', label: 'Stamp Photo' },
    ];

    const handleViewPhoto = async (fileName, photoType) => {
        if (!fileName) return;
        try {
            const token = localStorage.getItem('auth_token');
            const tenantId = localStorage.getItem('tenantId');
            const baseUrl = API_URL?.endsWith('/') ? API_URL : `${API_URL}/`;
            const response = await fetch(
                `${baseUrl}patient-medical-history/file-url?fileName=${fileName}`,
                {
                    method: 'GET',
                    headers: {
                        ...(token && { Authorization: `Bearer ${token}` }),
                        ...(tenantId && { tenantId }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setPhotoModal({
                    isOpen: true,
                    type: photoType,
                    currentPhoto: fileName,
                    photoUrl: data.signedUrl,
                });
            } else {
                toast.error('Failed to load photo');
            }
        } catch (error) {
            console.error('Error fetching photo:', error);
            toast.error('Failed to load photo');
        }
    };

    const closePhotoModal = () => {
        setPhotoModal({ isOpen: false, type: null, currentPhoto: null, photoUrl: null });
    };

    const isDoctorActive = (doctor) => {
        if (doctor?.active !== undefined) return doctor.active;
        return (doctor?.status || '').toUpperCase() === 'ACTIVE';
    };

    const handleToggleActive = async (doctor) => {
        console.log(doctor,"doctoruygiuh");
        const token = localStorage.getItem('auth_token');
        const tenantId = doctor?.tenantId
        const baseUrl = API_URL?.endsWith('/') ? API_URL : `${API_URL}/`;
        const DOCTOR_API_BASE_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
        const doctorId = getDoctoremail(doctor);
        const currentStatus = (doctor.status || '');
        const profileId = doctor?.doctorProfileId;
        const email = doctor?.email;
      

        let doctorCode 
        if ( email) {
            const profileRes = await fetch(`${baseUrl}doctor/profile/${email}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                    // ...(tenantId && { tenantId }),
                },
            });
            const profileData = await profileRes.json().catch(() => ({}));
            if (!profileRes.ok) {
                const errorMsg = profileData?.message || 'Failed to fetch doctor profile for status sync';
                toast.error(errorMsg);
                setDoctors(previousDoctors);
                return;
            }
            const profileDoctor = profileData?.data ?? profileData;
            console.log(profileDoctor,"profileDoctor");
            doctorCode = profileDoctor?.doctorCode ||  "";
        }
        // If current status is INACTIVE or VERIFIED → set to ACTIVE
        // If current status is ACTIVE → set to INACTIVE
        let newActive;
        if (currentStatus === 'ACTIVE') {
            newActive = 'INACTIVE';
        } else if (currentStatus === 'INACTIVE' || currentStatus === 'VERIFIED') {
            newActive = 'ACTIVE';
        } else {
            // For any other status, do not change
            toast.info('Status can be toggled only from ACTIVE/INACTIVE/VERIFIED.');
            return;
        }

        console.log(newActive, doctor.status, 'newActive');

        setTogglingDoctorId(doctorId);
        const previousDoctors = [...doctors];

        // Optimistically update UI so status text and toggle change immediately
        setDoctors((prev) =>
            prev.map((d) =>
                getDoctorId(d) === doctorId
                    ? {
                        ...d,
                        status: newActive,
                        active: newActive === 'ACTIVE',
                      }
                    : d
            )
        );
       

        try {
          
          

            const endpoint = `doctor/onboard-requests/${doctorId}/status?status=${newActive}`;
            console.log(endpoint,"endpoint");
            const res = await fetch(`${baseUrl}${endpoint}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });

            const data = await res.json().catch(() => ({}));
            console.log(data,res,"datajhbjk");
            if (res.status !== 200) {
                const errorMsg = data?.message || 'Failed to update doctor status';
                toast.error(errorMsg);
                setDoctors(previousDoctors);
                return;
            }

            const tenantRes = await fetch(`${baseUrl}${endpoint}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...(tenantId && { tenantId }),
                },
            });
            const tenantData = await tenantRes.json().catch(() => ({}));
            if (tenantRes.status !== 200) {
                const errorMsg = tenantData?.message || 'Failed to update doctor status for tenant';
                toast.error(errorMsg);
                setDoctors(previousDoctors);
                return;
            }

        
           

            if (doctorCode) {
                const statusRes = await fetch(
                    `${DOCTOR_API_BASE_URL}/api/status?doctorCode=${encodeURIComponent(doctorCode)}&status=${encodeURIComponent(newActive)}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                      
                        },
                    }
                );
                const statusData = await statusRes.json().catch(() => ({}));
                if (statusRes.status !== 200) {
                    const errorMsg = statusData?.message || 'Failed to sync doctor status';
                    toast.error(errorMsg);
                    setDoctors(previousDoctors);
                    return;
                }
            }

            toast.success(`Doctor status updated to ${newActive}`);
        } catch (error) {
            console.error('Failed to toggle active status:', error);
            setDoctors(previousDoctors);
            toast.error('Failed to update doctor status');
        } finally {
            setTogglingDoctorId(null);
            fetchList();
        }
    };

    const isDoctorVerified = (doctor) => {
        if (doctor?.verified !== undefined) return doctor.verified;
        if (doctor?.isVerified !== undefined) return doctor.isVerified;
        return (doctor?.status || '').toUpperCase() === 'VERIFIED';
    };

    const handleVerifyConfirm = async () => {
        if (!verifyConfirmDoctor) return;
        const doctorId = getDoctorId(verifyConfirmDoctor);

        setTogglingVerifiedDoctorId(doctorId);
        const previousDoctors = [...doctors];

        setDoctors((prev) =>
            prev.map((d) =>
                getDoctorId(d) === doctorId
                    ? { ...d, verified: true, isVerified: true, status: 'VERIFIED' }
                    : d
            )
        );
        setVerifyConfirmDoctor(null);

        try {
            const token = localStorage.getItem('auth_token');
            const baseUrl = API_URL?.endsWith('/') ? API_URL : `${API_URL}/`;
            const res = await fetch(`${baseUrl}onboarding/verify/${doctorId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });

            const data = await res.json().catch(() => ({}));
            console.log(data,"fghjkml");
            if (!res.ok || data.success === false) {
                const errorMsg = data?.message || 'Failed to verify doctor';
                toast.error(errorMsg);
                setDoctors(previousDoctors);
                return;
            }
            toast.success('Doctor verified successfully');
        } catch (error) {
            console.error('Failed to verify doctor:', error);
            setDoctors(previousDoctors);
            toast.error('Failed to verify doctor');
        } finally {
            setTogglingVerifiedDoctorId(null);
        }
    };

    const openFinanceApprovalConfirm = (doctor, financeApprovalStatus, label, pastLabel) => {
        setRejectRemark("");
        setFinanceApprovalConfirm({ doctor, financeApprovalStatus, label, pastLabel });
    };

    const handleFinanceApprovalConfirm = async () => {
        if (!financeApprovalConfirm) return;
        const { doctor, financeApprovalStatus, label, pastLabel } = financeApprovalConfirm;
        const remark = rejectRemark.trim();
        if (financeApprovalStatus === 2 && !remark) {
            toast.error('Please enter a reason for rejection');
            return;
        }
        const doctorId = getDoctorId(doctor);
        const email = getDoctoremail(doctor);
       
        setFinanceApprovalLoadingId(doctorId);
        const previousDoctors = [...doctors];

        setDoctors((prev) =>
            prev.map((d) =>
                getDoctorId(d) === doctorId ? { ...d, financeApprovalStatus } : d
            )
        );
        setFinanceApprovalConfirm(null);
        setRejectRemark("");

        try {
            const token = localStorage.getItem('auth_token');
            // const tenantId = localStorage.getItem('tenantId');
             const tenantId=doctor?.tenantId
            const baseUrl = API_URL?.endsWith('/') ? API_URL : `${API_URL}/`;

            const callFinanceApprovalApi = (includeTenantId) =>
                fetch(`${baseUrl}api/doctor-onboarding/finance-approval-status`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token && { Authorization: `Bearer ${token}` }),
                        ...(includeTenantId && tenantId && { tenantId }),
                    },
                    body: JSON.stringify({
                        email,
                        financeApprovalStatus,
                        ...(financeApprovalStatus === 2 && { rejectRemark: remark }),
                    }),
                });

            const res = await callFinanceApprovalApi(true);
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) {
                const errorMsg = data?.message || `Failed to ${label.toLowerCase()} doctor`;
                toast.error(errorMsg);
                setDoctors(previousDoctors);
                return;
            }

            try {
                await callFinanceApprovalApi(false);
            } catch (secondError) {
                console.error('Failed to update finance approval status (without tenantId):', secondError);
            }

            toast.success(`Doctor ${pastLabel} successfully`);
            window.location.reload();
        } catch (error) {
            console.error('Failed to update finance approval status:', error);
            setDoctors(previousDoctors);
            toast.error(`Failed to ${label.toLowerCase()} doctor`);
        } finally {
            setFinanceApprovalLoadingId(null);
        }
    };

    if (loading && doctors.length === 0) {
        return (
            <DashboardLayout>
                <div className="mt-6 flex justify-center items-center h-64">
                    <div className="text-lg text-[#004276]">Loading doctors...</div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <style>{`
                .doctor-list-table-scroll { scrollbar-width: thin; scrollbar-color: #94a3b8 #e2e8f0; }
                .doctor-list-table-scroll::-webkit-scrollbar { height: 10px; }
                .doctor-list-table-scroll::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 4px; }
                .doctor-list-table-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
                .doctor-list-table-scroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
            `}</style>
            <div className="mt-8 px-4 sm:px-6 min-w-0 w-full max-w-full ">
            <div className="flex items-center gap-4 mb-8">
            <div className="bg-emerald-50 p-2 rounded-xl border border-emerald-100 flex items-center justify-center">
              <Users size={26} className="text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#334155] tracking-tight">
                Doctor List
              </h1>
            </div>
          </div>
                {/* Search & filters card */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-5 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                        <SlidersHorizontal size={18} className="text-emerald-600 shrink-0" />
                        <h2 className="text-sm font-semibold text-slate-700 mb-0">Search & filters</h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                        <div>
                            <label className="block text-[13px] font-medium text-slate-600 mb-1.5">
                                Doctor name
                            </label>
                            <div className="relative">
                                <Search
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                                />
                                <input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Search by name"
                                    className="w-full h-11 pl-9 pr-3 border border-slate-200 rounded-xl bg-slate-50/50 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all hover:border-slate-300 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[13px] font-medium text-slate-600 mb-1.5">
                                Email
                            </label>
                            <div className="relative">
                                <Mail
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                                />
                                <input
                                    type="email"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    placeholder="Search by email"
                                    className="w-full h-11 pl-9 pr-3 border border-slate-200 rounded-xl bg-slate-50/50 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all hover:border-slate-300 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15"
                                />
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setSearchInput('');
                                setEmailInput('');
                                setStatusFilter('');
                                setFilter('all');
                                setCurrentPage(1);
                            }}
                            disabled={!searchInput && !emailInput && !statusFilter && filter === 'all'}
                            className="h-11 px-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl bg-white hover:bg-slate-50 hover:text-[#004276] disabled:opacity-40 disabled:cursor-not-allowed transition-colors sm:w-auto w-full"
                        >
                            <RotateCcw size={15} />
                            Reset
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
                        <div className="inline-flex p-1 bg-slate-100 rounded-xl gap-0.5">
                            {[
                                { value: 'all', label: 'All' },
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                            ].map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => {
                                        setFilter(value);
                                        setStatusFilter('');
                                        setCurrentPage(1);
                                    }}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                                        filter === value && !statusFilter
                                            ? 'bg-white text-[#004276] shadow-sm'
                                            : 'text-slate-600 hover:text-slate-800'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                    <div className="flex items-center gap-3 flex-wrap sm:justify-end">
                        {/* Custom date range picker */}
                        {/* <div className="relative mobile:w-full" ref={datePickerRef}>
                            <button 
                                type="button"
                                onClick={() => {
                                    setDatePickerOpen(!datePickerOpen);
                                }}
                                className="h-11 px-4 border border-slate-200 rounded-xl bg-slate-50/50 flex items-center gap-3 hover:bg-white hover:border-teal-500/50 transition-all mobile:w-full mobile:justify-between mobile:text-sm"
                            >
                                <Calendar size={14} className="text-teal-600 mobile:w-4 mobile:h-4" />
                                <span className="truncate">{formatSelectedDateLabel()}</span>
                                <ChevronLeft size={16} className={`${datePickerOpen ? 'rotate-90' : '-rotate-90'} transition-transform duration-200 mobile:w-4 mobile:h-4`} />
                            </button>
                            {datePickerOpen && (
                                <div className="absolute right-0 top-[calc(100%+8px)] bg-white border border-slate-200 rounded-2xl shadow-2xl p-5 w-80 z-10 mobile:right-auto mobile:left-0 mobile:w-full mobile:max-w-[calc(100vw-2rem)] animate-in fade-in zoom-in-95 duration-200">
                                    <div className="text-center mb-1">
                                        <div className="flex items-center justify-between mb-5">
                                            <button type="button" className="p-1.5 hover:bg-teal-50 rounded-lg transition-colors" onClick={handlePrevMonth}>
                                                <ChevronLeft size={20} className="text-teal-600" />
                                            </button>
                                            <span className="text-slate-800">
                                                {format(new Date(year, month, 1), "MMMM yyyy")}
                                            </span>
                                            <button type="button" className="p-1.5 hover:bg-teal-50 rounded-lg transition-colors" onClick={handleNextMonth}>
                                                <ChevronRight size={20} className="text-teal-600" />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-7 gap-1 mb-2">
                                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                                <div key={day} className="text-[10px] uppercase tracking-widest font-black text-slate-300 p-2">
                                                    {day}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1">
                                            {[...Array(firstDayOfMonth)].map((_, i) => (
                                                <div key={`empty-${i}`} className="p-2"></div>
                                            ))}
                                            {[...Array(daysInMonth)].map((_, i) => {
                                                const day = i + 1;
                                                const { start, end } = selectedRange;
                                                const current = new Date(year, month, day);
                                                const startDateOnly = start
                                                    ? new Date(start.getFullYear(), start.getMonth(), start.getDate())
                                                    : null;
                                                const endDateOnly = end
                                                    ? new Date(end.getFullYear(), end.getMonth(), end.getDate())
                                                    : null;
                                                const currentOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());

                                                const isStart =
                                                    startDateOnly &&
                                                    currentOnly.getTime() === startDateOnly.getTime();
                                                const isEnd =
                                                    endDateOnly &&
                                                    currentOnly.getTime() === endDateOnly.getTime();
                                                const inRange =
                                                    startDateOnly &&
                                                    endDateOnly &&
                                                    currentOnly >= startDateOnly &&
                                                    currentOnly <= endDateOnly;

                                                return (
                                                    <button
                                                        key={day}
                                                        type="button"
                                                        onClick={() => handleDayClick(day)}
                                                        className={`h-9 w-9 text-xs rounded-lg ${
                                                            isStart || isEnd
                                                                ? 'bg-teal-600 text-white font-black shadow-lg shadow-teal-200'
                                                                : inRange
                                                                ? 'bg-teal-50 text-teal-700'
                                                                : 'hover:bg-teal-50/50 text-slate-700'
                                                        }`}
                                                    >
                                                        {day}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div> */}

                        {/* Detailed status filter */}
                        <div className="relative" ref={statusFilterRef}>
                            <button
                                type="button"
                                onClick={() => setStatusFilterOpen((o) => !o)}
                                className={`h-10 flex items-center gap-2 px-4 rounded-xl border text-sm font-medium transition-colors ${
                                    statusFilter
                                        ? 'border-[#004276] bg-[#004276]/10 text-[#004276]'
                                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                                }`}
                                aria-haspopup="listbox"
                                aria-expanded={statusFilterOpen}
                                aria-label="Filter by status"
                            >
                                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                                <span>More status</span>
                                {statusFilter && (
                                    <span className="ml-1 text-xs bg-[#004276] text-white px-1.5 py-0.5 rounded">
                                        {statusFilter}
                                    </span>
                                )}
                            </button>
                            {statusFilterOpen && (
                                <div
                                    className="absolute right-0 top-full mt-1 z-20 min-w-[160px] py-1 bg-white border border-slate-200 rounded-xl shadow-lg"
                                    role="listbox"
                                >
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                        Status
                                    </div>
                                    {['', 'INITIATED', 'REGISTERED', 'VERIFIED', 'ACTIVE', 'INACTIVE'].map((value) => (
                                        <button
                                            key={value || 'all'}
                                            type="button"
                                            role="option"
                                            aria-selected={statusFilter === value}
                                            onClick={() => {
                                                setStatusFilter(value);
                                                setFilter('all');
                                                setCurrentPage(1);
                                                setStatusFilterOpen(false);
                                            }}
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                statusFilter === value ? 'bg-[#004276]/10 text-[#004276] font-medium' : 'text-gray-700'
                                            }`}
                                        >
                                            {value || 'All'}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    </div>
                </div>

                {/* Doctor Table */}
                <div ref={tableSectionRef} className="bg-white rounded-lg min-w-0">
                    {/* Mobile card list */}
                    <div className="md:hidden divide-y divide-gray-200">
                        {doctors.length === 0 ? (
                            <div className="px-4 py-8 text-center text-gray-500 text-sm">No doctors found</div>
                        ) : (
                            doctors.map((doctor, index) => {
                                const doctorId = getDoctorId(doctor);
                                const canToggleStatus =
                                    doctor?.status === "ACTIVE" ||
                                    doctor?.status === "INACTIVE" ||
                                    (doctor?.status === "VERIFIED" && doctor?.financeApprovalStatus === 1);
                                return (
                                    <div key={doctorId} className="p-4 flex flex-col gap-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-2 text-sm text-[#4B5563] min-w-0">
                                                <div className="h-6 w-6 rounded-lg bg-teal-600 flex items-center justify-center shadow flex-shrink-0">
                                                    <Stethoscope size={13} className="text-white" />
                                                </div>
                                                <span className="font-medium truncate">{getFullName(doctor)}</span>
                                            </div>
                                            <span
                                                className={`inline-flex flex-shrink-0 items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                                                    doctor?.registrationApprovalStatus === 2
                                                        ? 'bg-red-100 text-red-700 font-medium'
                                                        : getStatusStyle(doctor?.status)
                                                }`}
                                            >
                                                {doctor?.registrationApprovalStatus === 2
                                                    ? 'Rejected'
                                                    : doctor?.status === "ACTIVE" || doctor?.status === "INACTIVE"
                                                    ? getActiveInactive(doctor)
                                                    : formatStatusLabel(doctor?.status)}
                                                {doctor?.registrationApprovalStatus === 2 && doctor?.registrationRejectRemark && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setRejectRemarkView({ doctor, remark: doctor.registrationRejectRemark })}
                                                        title="View rejection reason"
                                                        className="inline-flex items-center justify-center rounded-full p-0.5 text-red-700 hover:bg-red-200/60 transition-colors"
                                                    >
                                                        <Eye size={12} />
                                                    </button>
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex flex-col gap-1 text-sm text-[#4B5563]">
                                            {doctor.mobile && (
                                                <div className="flex items-center gap-2">
                                                    <Phone size={13} className="text-emerald-600 flex-shrink-0" />
                                                    <span>{doctor.mobile}</span>
                                                </div>
                                            )}
                                            {doctor.email && (
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Mail size={13} className="text-emerald-600 flex-shrink-0" />
                                                    <span className="truncate">{doctor.email}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            {doctor?.status !== "REGISTERED" && (
                                            <div className="flex items-center gap-1.5">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${getFinanceStatusStyle(
                                                        doctor?.financeApprovalStatus
                                                    )}`}
                                                >
                                                    {getFinanceStatusLabel(doctor?.financeApprovalStatus)}
                                                    {doctor?.financeApprovalStatus === 2 && doctor?.rejectRemark && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setRejectRemarkView({ doctor, remark: doctor.rejectRemark })}
                                                            title="View rejection reason"
                                                            className="inline-flex items-center justify-center rounded-full p-0.5 text-red-700 hover:bg-red-200/60 transition-colors"
                                                        >
                                                            <Eye size={12} />
                                                        </button>
                                                    )}
                                                </span>
                                                {doctor?.financeApprovalStatus === 2 && !isFinanceRole && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openFinanceApprovalConfirm(doctor, 0, 'Resend for Approval', 'resent for finance verification')}
                                                        disabled={financeApprovalLoadingId === doctorId}
                                                        className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-full text-[11px] font-medium text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <RotateCcw size={12} />
                                                        Resend
                                                    </button>
                                                )}
                                            </div>
                                            )}
                                            {!isFinanceRole && (
                                                canToggleStatus ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openToggleStatusConfirm(doctor)}
                                                        disabled={togglingDoctorId === doctorId}
                                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#004276] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                                                            isDoctorActive(doctor) ? "bg-green-500" : "bg-gray-400"
                                                        }`}
                                                        role="switch"
                                                        aria-checked={isDoctorActive(doctor)}
                                                        aria-label={`Toggle ${getFullName(doctor)} ${
                                                            isDoctorActive(doctor) ? "inactive" : "active"
                                                        }`}
                                                    >
                                                        <span
                                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                                                                isDoctorActive(doctor) ? "translate-x-5" : "translate-x-0.5"
                                                            }`}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        disabled
                                                        className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent bg-gray-300 opacity-70 cursor-not-allowed"
                                                        aria-hidden="true"
                                                    >
                                                        <span className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 translate-x-0.5" />
                                                    </button>
                                                )
                                            )}
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {doctor.status === "REGISTERED" && !isFinanceRole && doctor?.registrationApprovalStatus !== 2 ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleOnboarding(doctor)}
                                                    className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-xl text-[12px] bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                                                >
                                                    <UserPlus size={14} />
                                                    Onboard
                                                </button>
                                            ) : doctor.status === "ACTIVE" || doctor.status === "INACTIVE" || doctor.financeApprovalStatus === 2 ? (
                                                <>
                                                    {!isFinanceRole && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEdit(doctor)}
                                                            className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-xl text-[12px] bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                                                        >
                                                            <Pencil size={13} />
                                                            Edit
                                                        </button>
                                                    )}
                                                </>
                                            ) : null}
                                            {doctor.status === "ACTIVE" || doctor.status === "INACTIVE" || doctor.status === "VERIFIED" ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleView(doctor)}
                                                    className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-xl text-[12px] bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                                                >
                                                    <Eye size={14} />
                                                    View
                                                </button>
                                            ) : null}
                                            {isFinanceRole && doctor?.status === "VERIFIED" && doctor?.financeApprovalStatus === 0 && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => openFinanceApprovalConfirm(doctor, 1, 'Approve', 'approved')}
                                                        disabled={financeApprovalLoadingId === doctorId}
                                                        className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-xl text-[12px] bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <CheckCircle size={14} />
                                                        Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openFinanceApprovalConfirm(doctor, 2, 'Reject', 'rejected')}
                                                        disabled={financeApprovalLoadingId === doctorId}
                                                        className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-xl text-[12px] bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <XCircle size={14} />
                                                        Reject
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div
                        className="hidden md:block doctor-list-table-scroll min-w-0 overflow-y-visible"
                        style={{
                            width: tableContainerWidth != null ? tableContainerWidth : '100%',
                            maxWidth: '100%',
                            overflowX: 'scroll',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <table className="divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
                            <thead className="bg-sky-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center gap-2">
                                        <Stethoscope size={14} className="text-teal-600" />
                                            Full Name
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <Phone size={14} className="text-emerald-600" />
                                            Mobile Number
                                        </div>
                                    </th>
                                   
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <Mail size={14} className="text-emerald-600" />
                                            Email
                                        </div>
                                    </th>
                                   
                                    {/* <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider">
                                        Active/Inactive
                                    </th> */}
                                    {/* <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider">
                                        Verified
                                    </th> */}
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <BadgeCheck size={14} className="text-emerald-600" />
                                            Current Status
                                        </div>
                                    </th>
                                     {/* <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <BadgeCheck size={14} className="text-emerald-600" />
                                            Pan Verified Status
                                        </div>
                                    </th>
                                     <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <BadgeCheck size={14} className="text-emerald-600" />
                                            Bank Verified Status
                                        </div>
                                    </th>
                                     <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <BadgeCheck size={14} className="text-emerald-600" />
                                            MCI Verified Status
                                        </div>
                                    </th> */}
                                     {!isFinanceRole && (
                                       <th className="px-6 py-3 text-center text-sm font-semibold text-[#4B5563]  tracking-wider">
                                            <div className="flex items-center justify-center gap-2">
                                                <BadgeCheck size={14} className="text-emerald-600" />
                                                Finance Status
                                            </div>
                                        </th>
                                     )}
                                    <th className="px-6 py-3 text-center text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center justify-center gap-2">
                                            <ToggleLeft size={14} className="text-emerald-600" />
                                            Change Status
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-center text-sm font-semibold text-[#4B5563]  tracking-wider">
                                        <div className="flex items-center justify-center gap-2">
                                            <Settings size={14} className="text-emerald-600" />
                                            Action
                                        </div>
                                    </th>
                                  
                                     
                                 
                                    {isFinanceRole && (
                                        <th className="px-6 py-3 text-center text-sm font-semibold text-[#4B5563]  tracking-wider">
                                            <div className="flex items-center justify-center gap-2">
                                                <CheckCircle size={14} className="text-emerald-600" />
                                                Finance Approval
                                            </div>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {doctors.length === 0 ? (
                                    <tr>
                                        <td colSpan={isFinanceRole ? 8 : 6} className="px-6 py-8 text-center text-gray-500">
                                            No doctors found
                                        </td>
                                    </tr>
                                ) : (
                                    doctors.map((doctor, index) => {
                                        const doctorId = getDoctorId(doctor);
                                       const canToggleStatus =
  doctor?.status === "ACTIVE" ||
  doctor?.status === "INACTIVE" ||
  (doctor?.status === "VERIFIED" && doctor?.financeApprovalStatus === 1);
                                        return (
                                            <tr key={doctorId} className={index % 2 === 0 ? 'bg-white' : 'bg-[#FDFDFD]'}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                                                        <div className="h-5 w-5 rounded-lg bg-teal-600 flex items-center justify-center shadow flex-shrink-0">
                                                            <Stethoscope size={12} className="text-white" />
                                                        </div>
                                                        <span>{getFullName(doctor)}</span>
                                                    </div>
                                                </td>
                                               
                                               
                                                {/* <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-[#4B5563]">
                                                        {formatLicenseNumber(doctor.medicalRegistrationNumber ?? doctor.medicalLicenseNumber)}
                                                    </div>
                                                </td> */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-[#4B5563]">
                                                        {doctor.mobile ? doctor.mobile:""}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                <div className="text-sm text-[#4B5563] max-w-xs">
                                                        {doctor.email ? doctor.email:""}
                                                    </div>
                                                    {/* <div className="text-sm text-[#4B5563] max-w-xs">
                                                        {formatSpecialization(doctor.speciality ?? doctor.specialization)}
                                                    </div> */}
                                                </td>
                                                {/* <td className="px-6 py-4 whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleActive(doctor)}
                                                        disabled={togglingDoctorId === doctorId}
                                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#004276] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                                                            isDoctorActive(doctor) ? 'bg-green-500' : 'bg-gray-400'
                                                        }`}
                                                        role="switch"
                                                        aria-checked={isDoctorActive(doctor)}
                                                        aria-label={`Toggle ${getFullName(doctor)} ${isDoctorActive(doctor) ? 'inactive' : 'active'}`}
                                                    >
                                                        <span
                                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                                                                isDoctorActive(doctor) ? 'translate-x-5' : 'translate-x-0.5'
                                                            }`}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                    <span className={`ml-2 text-sm font-medium ${getActiveInactiveStyle(doctor)}`}>
                                                        {getActiveInactive(doctor)}
                                                    </span>
                                                </td> */}
                                                {/* <td className="px-6 py-4 whitespace-nowrap">
                                                    {isDoctorVerified(doctor) ? (
                                                        <span className={`inline-block text-sm px-2.5 py-1 rounded-full font-medium ${getVerifiedStyle(doctor)}`}>
                                                            {getVerifiedStatus(doctor)}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => setVerifyConfirmDoctor(doctor)}
                                                            disabled={togglingVerifiedDoctorId === doctorId}
                                                            className={`inline-block text-sm px-2.5 py-1 rounded-full font-medium border-0 outline-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${getVerifiedStyle(doctor)}`}
                                                        >
                                                            {getVerifiedStatus(doctor)}
                                                        </button>
                                                    )}
                                                </td> */}
                                               
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full ${
                                                            doctor?.registrationApprovalStatus === 2
                                                                ? 'bg-red-100 text-red-700 font-medium'
                                                                : getStatusStyle(doctor?.status)
                                                        }`}
                                                    >
                                                        {doctor?.registrationApprovalStatus === 2
                                                            ? 'Rejected'
                                                            : doctor?.status === "ACTIVE" || doctor?.status === "INACTIVE"
                                                            ? getActiveInactive(doctor)
                                                            : formatStatusLabel(doctor?.status)}
                                                        {doctor?.registrationApprovalStatus === 2 && doctor?.registrationRejectRemark && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setRejectRemarkView({ doctor, remark: doctor.registrationRejectRemark })}
                                                                title="View rejection reason"
                                                                className="inline-flex items-center justify-center rounded-full p-0.5 text-red-700 hover:bg-red-200/60 transition-colors"
                                                            >
                                                                <Eye size={12} />
                                                            </button>
                                                        )}
                                                    </span>
                                                </td>
                                                {/* <td>{doctor?.panVerified==true?"Verified":"Not Verified"}</td>
                                               <td>{doctor?.bankVerified==true?"Verified":"Not Verified"}</td>
                                                  <td>{doctor?.mciVerificationStatus ? formatStatusLabel(doctor?.mciVerificationStatus) : "Not Verified"}</td> */}

                                                 <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        {doctor?.status !== "REGISTERED" && doctor?.panVerified === true && doctor?.bankVerified === true && (
                                                        <div className="flex flex-col items-center gap-1.5">
                                                            <span
                                                                className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full ${getFinanceStatusStyle(
                                                                    doctor?.financeApprovalStatus
                                                                )}`}
                                                            >
                                                                {getFinanceStatusLabel(doctor?.financeApprovalStatus)}
                                                                {doctor?.financeApprovalStatus === 2 && doctor?.rejectRemark && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setRejectRemarkView({ doctor, remark: doctor.rejectRemark })}
                                                                        title="View rejection reason"
                                                                        className="inline-flex items-center justify-center rounded-full p-0.5 text-red-700 hover:bg-red-200/60 transition-colors"
                                                                    >
                                                                        <Eye size={13} />
                                                                    </button>
                                                                )}
                                                            </span>
                                                            {doctor?.financeApprovalStatus === 2 && (
                                                                <>
                                                                 {!isFinanceRole && (

                                                                <button
                                                                    type="button"
                                                                    onClick={() => openFinanceApprovalConfirm(doctor, 0, 'Resend for Approval', 'resent for finance verification')}
                                                                    disabled={financeApprovalLoadingId === doctorId}
                                                                    className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded-full text-[11px] font-medium text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <RotateCcw size={12} />
                                                                    Resend For Finance Approval
                                                                </button>
                                                                 )}
                                                                 </>
                                                            )}
                                                        </div>
                                                        )}
                                                    </td>
                                                    {!isFinanceRole && (
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    {canToggleStatus ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => openToggleStatusConfirm(doctor)}
                                                            disabled={togglingDoctorId === doctorId}
                                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#004276] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                                                                isDoctorActive(doctor) ? "bg-green-500" : "bg-gray-400"
                                                            }`}
                                                            role="switch"
                                                            aria-checked={isDoctorActive(doctor)}
                                                            aria-label={`Toggle ${getFullName(doctor)} ${
                                                                isDoctorActive(doctor) ? "inactive" : "active"
                                                            }`}
                                                        >
                                                            <span
                                                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                                                                    isDoctorActive(doctor) ? "translate-x-5" : "translate-x-0.5"
                                                                }`}
                                                                aria-hidden="true"
                                                            />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            disabled
                                                            className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent bg-gray-300 opacity-70 cursor-not-allowed"
                                                            aria-hidden="true"
                                                        >
                                                            <span className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 translate-x-0.5" />
                                                        </button>
                                                    )}
                                                </td>
                                                    )}
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right align-middle">
                                                    <div className="flex flex-nowrap gap-3 justify-end items-center min-h-[40px]">
                                                        {/* <button
                                                            onClick={() => handleEdit(doctor)}
                                                            className="..."
                                                        >
                                                            Edit
                                                        </button> */}
                                                        {doctor.status === "REGISTERED" && !isFinanceRole && doctor?.registrationApprovalStatus !== 2 ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOnboarding(doctor)}
                                                                className="inline-flex items-center justify-center gap-2 h-10 w-[100px]  px-3 rounded-xl text-[12px]  bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                                                            >
                                                                <UserPlus size={15} />
                                                                Verify Doctor
                                                            </button>
                                                        ) :
                                                        // doctor.status === "INITIATED" ? (
                                                        //     <button
                                                        //         type="button"
                                                        //         onClick={() => handleOnboarding(doctor)}
                                                        //         className="inline-flex items-center justify-center gap-2 h-10 w-[100px] px-4 rounded-xl text-[12px] bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                                                        //     >
                                                        //         <Shield size={15} />
                                                        //         Verify
                                                        //     </button>
                                                        // ):
                                                        doctor.status === "ACTIVE" || doctor.status === "INACTIVE" || doctor.financeApprovalStatus === 2 ? (
                                                            <>
                                                            {!isFinanceRole && (
                                                             <button
                                                                type="button"
                                                                onClick={() => handleEdit(doctor)}
                                                                className="inline-flex items-center justify-center gap-2 h-10  px-4 rounded-xl text-[12px] w-[100px] bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                                                            >
                                                                <Pencil size={14} />
                                                                Edit
                                                            </button>
                                                            )}
                                                            </>
                                                           
                                                        ):
                                                        null}
                                                        {doctor.status === "ACTIVE" || doctor.status === "INACTIVE" || doctor.status === "VERIFIED" ? (
                                                           <button
                                                           type="button"
                                                           onClick={() => handleView(doctor)}
                                                           className="inline-flex items-center justify-center gap-2 h-10  px-4 rounded-xl text-[12px] w-[100px]  bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                                                       >
                                                           <Eye size={15} />
                                                           View
                                                       </button>
                                                        ):
                                                        null}

                                                    </div>
                                                </td>
                                               
                                                   
                                              
                                                {isFinanceRole && (
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center align-middle">
                                                        {doctor?.status === "VERIFIED" && doctor?.financeApprovalStatus === 0 && doctor?.panVerified === true && doctor?.bankVerified === true ? (
                                                            <div className="flex flex-nowrap gap-2 justify-center items-center min-h-[40px]">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openFinanceApprovalConfirm(doctor, 1, 'Approve', 'approved')}
                                                                    disabled={financeApprovalLoadingId === doctorId}
                                                                    className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-xl text-[12px] bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <CheckCircle size={14} />
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openFinanceApprovalConfirm(doctor, 2, 'Reject', 'rejected')}
                                                                    disabled={financeApprovalLoadingId === doctorId}
                                                                    className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-xl text-[12px] bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <XCircle size={14} />
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Verify Confirmation Modal */}
                    {verifyConfirmDoctor && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="verify-modal-title">
                            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                                <h3 id="verify-modal-title" className="text-lg font-semibold text-[#004276] mb-4">
                                    Verify Doctor
                                </h3>
                                <p className="text-[#4B5563] mb-6">
                                    Are you sure you want to verify Dr {getFullName(verifyConfirmDoctor)}?
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setVerifyConfirmDoctor(null)}
                                        className="px-4 py-2 text-sm font-medium text-[#6B7280] bg-gray-100 hover:bg-gray-200 rounded-md"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleVerifyConfirm}
                                        className="px-4 py-2 text-sm font-medium text-white bg-[#004276] hover:bg-[#003060] rounded-md"
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Status Toggle Confirmation Modal */}
                    {toggleStatusConfirm?.doctor && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="toggle-status-modal-title">
                            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                                <h3 id="toggle-status-modal-title" className="text-lg font-semibold text-[#004276] mb-4">
                                    Change Doctor Status
                                </h3>
                                <p className="text-[#4B5563] mb-6">
                                    Are you sure you want to change Dr {getFullName(toggleStatusConfirm.doctor)} status to{' '}
                                    <span className="font-semibold">{toggleStatusConfirm.nextStatus}</span>?
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setToggleStatusConfirm(null)}
                                        className="px-4 py-2 text-sm font-medium text-[#6B7280] bg-gray-100 hover:bg-gray-200 rounded-md"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            const d = toggleStatusConfirm.doctor;
                                            setToggleStatusConfirm(null);
                                            await handleToggleActive(d);
                                        }}
                                        disabled={togglingDoctorId === getDoctorId(toggleStatusConfirm.doctor)}
                                        className="px-4 py-2 text-sm font-medium text-white bg-[#004276] hover:bg-[#003060] rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {togglingDoctorId === getDoctorId(toggleStatusConfirm.doctor) ? 'Updating...' : 'Yes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Finance Approval Confirmation Modal */}
                    {financeApprovalConfirm?.doctor && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="finance-approval-modal-title">
                            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                                <h3 id="finance-approval-modal-title" className="text-lg font-semibold text-[#004276] mb-4">
                                    {financeApprovalConfirm.label} Doctor
                                </h3>
                                <p className="text-[#4B5563] mb-4">
                                    Are you sure you want to {financeApprovalConfirm.label.toLowerCase()} Dr {getFullName(financeApprovalConfirm.doctor)}?
                                </p>
                                {financeApprovalConfirm.financeApprovalStatus === 2 && (
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-[#4B5563] mb-1">
                                            Reason for rejection
                                        </label>
                                        <textarea
                                            value={rejectRemark}
                                            onChange={(e) => setRejectRemark(e.target.value)}
                                            rows={3}
                                            placeholder="Enter reason for rejection"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-[#4B5563] focus:outline-none focus:ring-2 focus:ring-[#004276]"
                                        />
                                    </div>
                                )}
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setFinanceApprovalConfirm(null)}
                                        className="px-4 py-2 text-sm font-medium text-[#6B7280] bg-gray-100 hover:bg-gray-200 rounded-md"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleFinanceApprovalConfirm}
                                        disabled={
                                            financeApprovalLoadingId === getDoctorId(financeApprovalConfirm.doctor) ||
                                            (financeApprovalConfirm.financeApprovalStatus === 2 && !rejectRemark.trim())
                                        }
                                        className="px-4 py-2 text-sm font-medium text-white bg-[#004276] hover:bg-[#003060] rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {financeApprovalLoadingId === getDoctorId(financeApprovalConfirm.doctor) ? 'Updating...' : 'Yes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reject Remark Modal */}
                    {rejectRemarkView?.doctor && (
                        <div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="reject-remark-modal-title"
                            onClick={() => setRejectRemarkView(null)}
                        >
                            <div
                                className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <h3 id="reject-remark-modal-title" className="text-lg font-semibold text-[#004276] mb-4">
                                    Rejection Reason
                                </h3>
                                <p className="text-sm text-gray-500 mb-2">
                                    Dr {getFullName(rejectRemarkView.doctor)}
                                </p>
                                <p className="text-[#4B5563] mb-6 whitespace-pre-wrap break-words">
                                    {rejectRemarkView.remark}
                                </p>
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setRejectRemarkView(null)}
                                        className="px-4 py-2 text-sm font-medium text-[#6B7280] bg-gray-100 hover:bg-gray-200 rounded-md"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Doctor Details Side Panel */}
                    {viewDoctorDetails && (
                        <div
                            className="fixed inset-0 z-40 flex justify-end bg-black/40"
                            onClick={() => setViewDoctorDetails(null)}
                        >
                            <div
                                className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Top close icon inside sidebar */}
                                <div className="flex items-center justify-end px-6 py-4 border-b border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setViewDoctorDetails(null)}
                                        className="text-gray-500 hover:text-gray-700"
                                        aria-label="Close details"
                                    >
                                        <span className="text-2xl leading-none">&times;</span>
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-8">
                                    {viewDoctorLoading && (
                                        <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                                            Loading doctor details...
                                        </div>
                                    )}

                                    {!viewDoctorLoading && (
                                        <>
                                            {/* Header & Basic Info */}
                                            <div className="space-y-3">
                                                <h2 className="text-lg font-semibold text-[#111827]">
                                                    Doctor Details
                                                </h2>

                                                <div className="rounded-2xl border border-slate-200 bg-white px-5 py-5 text-sm text-gray-700 shadow-sm space-y-4">
                                                    {/* Name */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 pb-3 border-b border-slate-100">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Name</p>
                                                        <p className="text-base font-semibold text-slate-900">
                                                            {getFullName(viewDoctorDetails)}
                                                        </p>
                                                    </div>

                                                    {/* Contact & Personal */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8">
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile Number</p>
                                                            <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.phoneNumber || viewDoctorDetails.mobileNumber || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</p>
                                                            <p className="mt-1 font-medium text-slate-800 break-all">{viewDoctorDetails.email || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Age</p>
                                                            <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.age != null ? viewDoctorDetails.age : '-'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gender</p>
                                                            <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.gender || '-'}</p>
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Address</p>
                                                            <p className="mt-1 font-medium text-slate-800">
                                                                {[
                                                                    viewDoctorDetails.address,
                                                                    viewDoctorDetails.city,
                                                                    viewDoctorDetails.state,
                                                                    viewDoctorDetails.pinCode,
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(', ') || '-'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Professional info */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8">
                                                       
                                                          <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Medical License Number</p>
                                                        <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.medicalLicenseNumber || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Specialization</p>
                                                            <p className="mt-1 font-medium text-slate-800">
                                                                {Array.isArray(viewDoctorDetails.specialization)
                                                                    ? viewDoctorDetails.specialization.join(', ')
                                                                    : viewDoctorDetails.specialization || '-'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Doctor Type</p>
                                                            <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.doctorType != null ? viewDoctorDetails.doctorType : '-'}{viewDoctorDetails.isFamilyDoctor? <span className='inline-block ml-2 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium'>Family Doctor</span> : null}</p>
                                                        </div>
                                                        {(viewDoctorDetails.doctorType === 'EMPANELLED' || viewDoctorDetails.doctorType === 'INVENTORY') && (
                                                            <div>
                                                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Doctor Sub Type</p>
                                                                <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.doctorSubType || '-'}</p>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Experience (Years)</p>
                                                            <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.yearsOfExperience != null ? viewDoctorDetails.yearsOfExperience : '-'}</p>
                                                        </div>

                                                       <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Qualifications</p>
                                                            <p className="mt-1 font-medium text-slate-800">
                                                                {Array.isArray(viewDoctorDetails.qualifications)
                                                                    ? viewDoctorDetails.qualifications.join(', ')
                                                                    : viewDoctorDetails.qualifications || '-'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bio</p>
                                                        <p className="mt-1 text-slate-700 leading-relaxed">{viewDoctorDetails.bio || '-'}</p>
                                                        </div>
                                                        
                                                       
                                                    </div>

                                                    {/* Consultation / Commission */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8">
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Commission Percentage</p>
                                                            <p className="mt-1 font-medium text-slate-800">
                                                                {viewDoctorDetails.commissionPercentage != null
                                                                    ? `${viewDoctorDetails.commissionPercentage}`
                                                                    : '-'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payment Period</p>
                                                            <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.paymentPeriod || '-'}</p>
                                                        </div>
                                                    </div>

                                                    {/* Verification Status */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Pan Verified Status</p>
                                                            <span
                                                                className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full font-medium ${
                                                                    viewDoctorDetails.panVerified === true
                                                                        ? 'bg-emerald-100 text-emerald-700'
                                                                        : 'bg-amber-100 text-amber-700'
                                                                }`}
                                                            >
                                                                {viewDoctorDetails.panVerified === true ? "Verified" : "Not Verified"}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Bank Verified Status</p>
                                                            <span
                                                                className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full font-medium ${
                                                                    viewDoctorDetails.bankVerified === true
                                                                        ? 'bg-emerald-100 text-emerald-700'
                                                                        : 'bg-amber-100 text-amber-700'
                                                                }`}
                                                            >
                                                                {viewDoctorDetails.bankVerified === true ? "Verified" : "Not Verified"}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">MCI Verified Status</p>
                                                            <span
                                                                className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full font-medium ${
                                                                    (viewDoctorDetails.mciVerificationStatus || '').toUpperCase() === 'VERIFIED'
                                                                        ? 'bg-emerald-100 text-emerald-700'
                                                                        : (viewDoctorDetails.mciVerificationStatus || '').toUpperCase() === 'REJECTED'
                                                                        ? 'bg-red-100 text-red-700'
                                                                        : (viewDoctorDetails.mciVerificationStatus || '').toUpperCase() === 'NOT_APPLICABLE'
                                                                        ? 'bg-sky-100 text-sky-700'
                                                                        : 'bg-amber-100 text-amber-700'
                                                                }`}
                                                            >
                                                                {viewDoctorDetails.mciVerificationStatus ? formatStatusLabel(viewDoctorDetails.mciVerificationStatus) : "Not Verified"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                                {/* Bank Details */}
                                            <div>
                                                <h3 className="text-base font-semibold text-[#004276] mb-3">
                                                    Bank Details
                                                </h3>
                                                <div className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm text-gray-700">
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bank Name</p>
                                                        <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.bankName || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Account Holder Name</p>
                                                        <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.accountHolderName || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">IFSC Code</p>
                                                        <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.ifscCode || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Account Number</p>
                                                        <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.accountNumber || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bank Location</p>
                                                        <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.bankLoc || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">PAN Card Number</p>
                                                        <p className="mt-1 font-medium text-slate-800">{viewDoctorDetails.panCardNumber || '-'}</p>
                                                    </div>
                                                </div>
                                                </div>
                                            </div>

                                            {/* Time Slots */}
                                            {/* <div>
                                                <h3 className="text-base font-semibold text-[#004276] mb-2">
                                                    Time Slots
                                                </h3>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    Slot Duration:{" "}
                                                    <span className="inline-flex items-center rounded-full bg-teal-50 px-2.5 py-0.5 font-semibold text-teal-700">
                                                        {viewDoctorDetails.slotDuration || "-"}
                                                    </span>
                                                </p>
                                                {Array.isArray(viewDoctorDetails.timeSlots) && viewDoctorDetails.timeSlots.length > 0 ? (
                                                    <div className="space-y-3">
                                                        {viewDoctorDetails.timeSlots.map((slot, idx) => (
                                                            <div
                                                                key={idx}
                                                                className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm transition hover:shadow-md"
                                                            >
                                                                <div className="grid grid-cols-1 gap-4 text-sm text-gray-700 sm:grid-cols-2">
                                                                    <div>
                                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Day</p>
                                                                        <p className="mt-1 font-semibold text-slate-800">{slot.dayOfWeek || slot.day || "-"}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Time</p>
                                                                        <p className="mt-1 font-semibold text-slate-800">
                                                                            {slot.startTime && slot.endTime
                                                                                ? `${slot.startTime} - ${slot.endTime}`
                                                                                : "-"}
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Consultation Type</p>
                                                                        <p className="mt-1">
                                                                            <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                                                                                {formatConsultationType(slot.consultationType)}
                                                                            </span>
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Week Numbers</p>
                                                                        <p className="mt-1 font-semibold text-slate-800">
                                                                            {Array.isArray(slot.weekNumbers) && slot.weekNumbers.length > 0
                                                                                ? slot.weekNumbers.join(", ")
                                                                                : "-"}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-gray-500">No time slots available.</p>
                                                )}
                                            </div> */}

                                            {/* Documents */}
                                            <div>
                                                <h3 className="text-base font-semibold text-[#004276] mb-2">
                                                    Documents
                                                </h3>
                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                    {DOCUMENT_PHOTO_FIELDS.filter(({ key }) => viewDoctorDetails?.[key]).map(({ key, label }) => {
                                                        const fileName = viewDoctorDetails?.[key];
                                                        return (
                                                            <div
                                                                key={key}
                                                                className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
                                                            >
                                                                <span className="text-sm font-medium text-slate-700">{label}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleViewPhoto(fileName, key)}
                                                                    className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                                                                >
                                                                    <Eye size={13} />
                                                                    View
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Terms of Engagement Document */}
                                            {viewDoctorDetails?.agreementPdfUrl && (
                                                <div>
                                                    <h3 className="text-base font-semibold text-[#004276] mb-2">
                                                        Terms of Engagement Document
                                                    </h3>
                                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                                                            <span className="text-sm font-medium text-slate-700">Terms of Engagement Document</span>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setPhotoModal({
                                                                        isOpen: true,
                                                                        type: 'agreementPdf',
                                                                        currentPhoto: 'Terms of Engagement Document',
                                                                        photoUrl: viewDoctorDetails.agreementPdfUrl,
                                                                    })
                                                                }
                                                                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                                                            >
                                                                <Eye size={13} />
                                                                View
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Bottom close button */}
                                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setViewDoctorDetails(null)}
                                        className="px-4 py-2 text-sm font-medium text-[#004276] border border-[#004276] rounded-lg hover:bg-[#004276] hover:text-white transition"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 0 && (
                        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-white rounded-b-lg">
                            {/* Mobile: compact prev/next + page indicator */}
                            <div className="flex sm:hidden items-center justify-between gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`text-sm px-3 py-1.5 rounded-lg border ${currentPage === 1 ? 'text-[#9CA3AF] border-gray-200 cursor-not-allowed' : 'text-[#4B5563] border-gray-300 hover:bg-gray-50'}`}
                                >
                                    &lt; Prev
                                </button>
                                <div className="text-sm text-[#6B7280]">
                                    Page {currentPage} of {totalPages}
                                </div>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`text-sm px-3 py-1.5 rounded-lg border ${currentPage === totalPages ? 'text-[#9CA3AF] border-gray-200 cursor-not-allowed' : 'text-[#4B5563] border-gray-300 hover:bg-gray-50'}`}
                                >
                                    Next &gt;
                                </button>
                            </div>
                            <div className="mt-2 text-center text-xs text-[#6B7280] sm:hidden">
                                Showing {startEntry} to {endEntry} of {totalElements} entries
                            </div>

                            {/* Desktop / tablet: full pagination */}
                            <div className="hidden sm:flex sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`text-sm ${currentPage === 1 ? 'text-[#6B7280] cursor-not-allowed' : 'text-[#6B7280] hover:text-[#4B5563]'}`}
                                    >
                                        &lt; Prev
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {getPageNumbers().map((pageNum) => (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-3 py-1 text-sm ${
                                                    currentPage === pageNum
                                                        ? 'border border-[#4B5563] text-[#4B5563] bg-white'
                                                        : 'text-[#6B7280] hover:text-[#4B5563]'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`text-sm ${currentPage === totalPages ? 'text-[#6B7280] cursor-not-allowed' : 'text-[#6B7280] hover:text-[#4B5563]'}`}
                                    >
                                        Next &gt;
                                    </button>
                                </div>
                                <div className="text-sm text-[#6B7280]">
                                    Showing {startEntry} to {endEntry} of {totalElements} entries
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Photo Modal */}
            {photoModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closePhotoModal}>
                    <div className="relative w-[70vw] h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={closePhotoModal}
                            className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-70 transition-colors"
                        >
                            ×
                        </button>
                        {photoModal.photoUrl && (
                            <div className="bg-white rounded-lg overflow-hidden w-full h-full">
                                {photoModal.photoUrl.includes('.pdf') ? (
                                    <iframe
                                        src={photoModal.photoUrl}
                                        className="w-full h-full border-0"
                                        title="Document"
                                    />
                                ) : (
                                    <img
                                        src={photoModal.photoUrl}
                                        alt="Document preview"
                                        className="w-full h-full object-contain"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default CurebayDoctorList;
